import React, { useState } from 'react';
import { Loader, Modal, Dimmer, Icon } from 'semantic-ui-react';
import { SelectableModalProps } from '../../../../../components/types';
import styled from 'styled-components';
import FollowUpTab from '../Tabs/FollowUpTab';
import ActionHistoryTab from '../Tabs/ActionHistoryTab';
import PortoLogsTab from '../Tabs/PortoLogsTab';
import GaranttiLogsTab from '../Tabs/GaranttiLogsTab';

const CLAIM_UPDATES_ACCENT_COLOR = '#59249B';
const GARANTTI_ORANGE = '#f2711c';
const PORTO_INSURER_CODES = ['PORTO', 'PORTO_ESSENCIAL'];

const StyledModal = styled(Modal)`
  &&& {
    width: min(1440px, 96vw) !important;
  }

  &&& > .header {
    color: #2f3441;
    border-bottom: 1px solid #e5e7eb;
  }

  &&& > .header i.icon {
    color: ${CLAIM_UPDATES_ACCENT_COLOR};
  }

  &&& > .scrolling.content {
    max-height: calc(92vh - 6rem);
  }
`;

const TabsSection = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  margin: 0 -1.5rem 22px;
  padding: 0 1.5rem 20px;
`;

const TabContainer = styled.div<{ $tabs: number }>`
  border: 1px solid #dddddd;
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  width: ${props => (props.$tabs === 3 ? '400px' : '224px')};
  max-width: 100%;
  min-width: ${props => (props.$tabs === 3 ? 'min(400px, 100%)' : '224px')};
`;

const TabMenu = styled.div<{ actived: boolean; $tabs: number }>`
  width: ${props => (props.$tabs === 3 ? '33.34%' : '50%')};
  min-width: 0;
  padding: 8px 10px;
  background-color: ${props => (props.actived ? CLAIM_UPDATES_ACCENT_COLOR : '#ffffff')};
  color: ${props => (props.actived ? '#ffffff' : CLAIM_UPDATES_ACCENT_COLOR)};
  cursor: ${props => (props.actived ? '' : 'pointer')};
  font-weight: ${props => (props.actived ? 700 : 400)};
  text-align: center;
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${GARANTTI_ORANGE};
  flex-shrink: 0;
`;

export type FollowUpModalProps = {
  onClose: () => any;
  onOpen: () => any;
  id: string;
  claim: any;
  isBlocked?: boolean;
  user: any;
  initialTab?: TabProps;
} & SelectableModalProps;

export type TabProps = 'follow-up' | 'action-history' | 'porto' | 'garantti';

const isPortoClaim = (claim: any): boolean => {
  const code = String(claim?.insurerCode || '').toUpperCase();
  return PORTO_INSURER_CODES.includes(code);
};

const isGaranttiClaim = (claim: any): boolean =>
  String(claim?.insurerCode || '').toUpperCase() === 'GARANTTI';

const headerIcon = (tab: TabProps) => {
  if (tab === 'porto') {
    return 'building';
  }
  if (tab === 'garantti') {
    return 'comments';
  }
  return tab === 'follow-up' ? 'shield alternate' : 'history';
};

const resolveInitialTab = (
  initialTab: TabProps | undefined,
  showPortoTab: boolean,
  showGaranttiTab: boolean
): TabProps => {
  if (initialTab === 'garantti' && showGaranttiTab) {
    return 'garantti';
  }
  if (initialTab === 'porto' && showPortoTab) {
    return 'porto';
  }
  if (initialTab === 'action-history') {
    return 'action-history';
  }
  return 'follow-up';
};

const ClaimUpdatesListModal = (props: FollowUpModalProps) => {
  const { header, onClose, onOpen, isOpen, id, claim, user, isBlocked, initialTab } = props;
  const [isLoading] = useState<boolean>(false);
  const hasActionHistoryPermission = !user.role.includes('ESTATE');
  const showPortoTab = hasActionHistoryPermission && isPortoClaim(claim);
  const showGaranttiTab = hasActionHistoryPermission && isGaranttiClaim(claim);
  const tabCount = showPortoTab || showGaranttiTab ? 3 : 2;
  const [tabActive, setTabActive] = useState<TabProps>(
    resolveInitialTab(initialTab, showPortoTab, showGaranttiTab)
  );
  const showGaranttiTabDot = Number(claim?.unreadGaranttiLogsCount || 0) > 0;

  const handleDisplay = (hasPermission: boolean) => {
    const classFirstTab = {
      borderRight: '1px solid #dddddd',
      borderTopLeftRadius: '4px',
      borderBottomLeftRadius: '4px'
    };
    const classMiddleTab = {
      borderRight: '1px solid #dddddd'
    };
    const classLastTab = {
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px'
    };
    const classTabActive = { backgroundColor: CLAIM_UPDATES_ACCENT_COLOR, color: '#FFFFFF' };
    const classTabInactive = { cursor: 'pointer' };

    const classTabFollowup =
      tabActive === 'follow-up'
        ? Object.assign({}, classFirstTab, classTabActive)
        : Object.assign({}, classFirstTab, classTabInactive);

    const historyBase = showPortoTab || showGaranttiTab ? classMiddleTab : classLastTab;
    const classTabHistory =
      tabActive === 'action-history'
        ? Object.assign({}, historyBase, classTabActive)
        : Object.assign({}, historyBase, classTabInactive);

    const classTabInsurer =
      tabActive === 'porto' || tabActive === 'garantti'
        ? Object.assign({}, classLastTab, classTabActive)
        : Object.assign({}, classLastTab, classTabInactive);

    return hasPermission ? (
      <>
        <TabsSection>
          <TabContainer $tabs={tabCount}>
            <TabMenu
              actived={tabActive === 'follow-up'}
              $tabs={tabCount}
              style={classTabFollowup}
              onClick={() => setTabActive('follow-up')}
            >
              Geral
            </TabMenu>
            <TabMenu
              actived={tabActive === 'action-history'}
              $tabs={tabCount}
              style={classTabHistory}
              onClick={() => setTabActive('action-history')}
            >
              Histórico
            </TabMenu>
            {showPortoTab && (
              <TabMenu
                actived={tabActive === 'porto'}
                $tabs={tabCount}
                style={classTabInsurer}
                onClick={() => setTabActive('porto')}
              >
                PORTO
              </TabMenu>
            )}
            {showGaranttiTab && (
              <TabMenu
                actived={tabActive === 'garantti'}
                $tabs={tabCount}
                style={classTabInsurer}
                onClick={() => setTabActive('garantti')}
              >
                <TabLabel>
                  Garantti
                  {showGaranttiTabDot ? (
                    <UnreadDot aria-hidden="true" title="Atualizações não lidas da Garantti" />
                  ) : null}
                </TabLabel>
              </TabMenu>
            )}
          </TabContainer>
        </TabsSection>

        {tabActive === 'follow-up' ? (
          <FollowUpTab id={id} claim={claim} isBlocked={isBlocked} />
        ) : tabActive === 'action-history' ? (
          <ActionHistoryTab id={id} />
        ) : tabActive === 'porto' && showPortoTab ? (
          <PortoLogsTab id={id} claim={claim} />
        ) : tabActive === 'garantti' && showGaranttiTab ? (
          <GaranttiLogsTab id={id} claim={claim} />
        ) : null}
      </>
    ) : (
      <FollowUpTab id={id} claim={claim} isBlocked={isBlocked} />
    );
  };

  return (
    <>
      <StyledModal size="large" open={isOpen} onOpen={onOpen} onClose={onClose} closeIcon>
        <Modal.Header>
          {hasActionHistoryPermission ? (
            <>
              <Icon name={headerIcon(tabActive)} />{' '}
            </>
          ) : null}
          {header}
        </Modal.Header>
        <Modal.Content scrolling>
          {isLoading ? (
            <Dimmer active={isLoading} inverted>
              <Loader content="Carregando mensagens..." />
            </Dimmer>
          ) : (
            handleDisplay(hasActionHistoryPermission)
          )}
        </Modal.Content>
        <Modal.Actions></Modal.Actions>
      </StyledModal>
    </>
  );
};

export default ClaimUpdatesListModal;
